import React, {createContext, useContext} from 'react';
import {useQuery, useMutation} from '@apollo/client';
import {MY_ORGANIZATIONS, ACTIVE_ORGANIZATION} from '../graphql/queries';
import {SET_ACTIVE_ORGANIZATION} from '../graphql/mutations';
import {useAuth} from './AuthContext';
import {auth} from '../config/firebase';

interface Organization {
  id: string;
  name: string;
  slug: string;
  website?: string;
  createdAt: string;
}

interface OrgContextType {
  activeOrg: Organization | null;
  /**
   * Caller's role in `activeOrg` (e.g. "OWNER", "ADMIN", "MEMBER").
   * Null when there is no active org. Used by S8 admin-gated UI to
   * hide "Connect" / "Revoke" affordances from non-admins. Server
   * still enforces via `requireOrgAdmin`.
   */
  activeOrgRole: string | null;
  /** True iff `activeOrgRole` is OWNER or ADMIN. */
  isActiveOrgAdmin: boolean;
  organizations: Organization[];
  loading: boolean;
  switchOrg: (orgId: string) => Promise<void>;
  refetchOrgs: () => void;
}

const OrgContext = createContext<OrgContextType | undefined>(undefined);

export function OrgProvider({children}: {children: React.ReactNode}) {
  const {user} = useAuth();

  const {data: activeData, loading: activeLoading, refetch: refetchActive} = useQuery(
    ACTIVE_ORGANIZATION,
    {skip: !user}
  );

  const {data: orgsData, loading: orgsLoading, refetch: refetchOrgs} = useQuery(
    MY_ORGANIZATIONS,
    {skip: !user}
  );

  const [setActiveOrgMutation] = useMutation(SET_ACTIVE_ORGANIZATION);

  const activeOrg: Organization | null = activeData?.activeOrganization?.organization || null;
  const activeOrgRole: string | null = activeData?.activeOrganization?.role || null;
  const isActiveOrgAdmin =
    !!activeOrgRole && ['OWNER', 'ADMIN'].includes(activeOrgRole.toUpperCase());
  const organizations: Organization[] = orgsData?.myOrganizations || [];

  const switchOrg = async (orgId: string) => {
    const token = await auth.currentUser?.getIdToken();
    if (!token) return;

    await setActiveOrgMutation({
      variables: {orgId},
      context: {headers: {authorization: `Bearer ${token}`}},
    });

    await refetchActive();
  };

  const handleRefetchOrgs = () => {
    refetchOrgs();
    refetchActive();
  };

  const value: OrgContextType = {
    activeOrg,
    activeOrgRole,
    isActiveOrgAdmin,
    organizations,
    loading: activeLoading || orgsLoading,
    switchOrg,
    refetchOrgs: handleRefetchOrgs,
  };

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
}

export function useOrg() {
  const context = useContext(OrgContext);
  if (context === undefined) {
    throw new Error('useOrg must be used within an OrgProvider');
  }
  return context;
}
